import type { AppFeatures, AppKind } from "common/components/SerializedApp";
import { getAppKindFromFeatures } from "common/components/SerializedApp";
import type {
    AppUserAuthenticator,
    DataStore,
    MinimalAppEnvironment,
    UserProfileTableInfo,
} from "common/components/types";
import type { Database } from "common/Database";
import { areTableNamesEqual } from "common/database-strings";
import type { EminenceFlags } from "common/Database/eminence";
import type { LocationSettings } from "common/location-common";
import type { SourceMetadata } from "common/source-metadata";
import type { YesCodeValue } from "common/yes-code-types";
import { getBuilderEminenceFlags } from "hooks/use-builder-eminence-flags";
import { getPlayerEminenceFlags } from "hooks/use-player-eminence-flags";
import { getAppFacilities } from "webapp/lib/app-renderer";

export class WireAppEnvironment implements MinimalAppEnvironment {
    public readonly appFacilities = getAppFacilities();

    constructor(
        private features: AppFeatures,
        private sourceMetadataArray: readonly SourceMetadata[] | undefined,
        private userProfile: UserProfileTableInfo | undefined,
        public readonly authenticator: AppUserAuthenticator,
        public readonly database: Database,
        public readonly dataStore: DataStore,
        public readonly isBuilder: boolean
    ) {}

    public get appID(): string {
        return this.authenticator.appID;
    }

    public get appFeatures(): AppFeatures {
        return this.features;
    }

    public get appKind(): AppKind {
        return getAppKindFromFeatures(this.appFeatures);
    }

    public get userProfileTableInfo(): UserProfileTableInfo | undefined {
        return this.userProfile;
    }

    public get sourceMetadata(): readonly SourceMetadata[] | undefined {
        return this.sourceMetadataArray;
    }

    // FIXME: This method should be async to prevent default Eminence Flags from being returned
    public get eminenceFlags(): EminenceFlags {
        return this.isBuilder ? getBuilderEminenceFlags(this.appID, undefined) : getPlayerEminenceFlags(this.appID);
    }

    public get locationSettings(): LocationSettings | undefined {
        return this.appFacilities.locationSettings;
    }

    public callYesCode(
        url: string,
        params: ReadonlyMap<string, unknown>
    ): YesCodeValue | undefined | Promise<YesCodeValue | undefined> {
        return this.appFacilities.callYesCode(url, params);
    }

    public isGeocodeWithinQuota(_key: string): boolean {
        // TODO: Implement when we have components that use this.
        return true;
    }

    public updateApp(
        appFeatures: AppFeatures,
        sourceMetadata: readonly SourceMetadata[] | undefined,
        userProfileTableInfo: UserProfileTableInfo | undefined
    ): void {
        this.features = appFeatures;

        this.sourceMetadataArray = sourceMetadata;

        const isSameTable = areTableNamesEqual(this.userProfile?.tableName, userProfileTableInfo?.tableName);
        this.userProfile = userProfileTableInfo;
        if (!isSameTable) {
            this.dataStore.userProfileTableChanged();
        }
    }
}
